/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Observable, throwError as observableThrowError } from 'rxjs';

// local imports
import { ApiService } from 'app/services/api.service';
import { IApiResponse, ICompany } from '@truckstop-tms/zuum-lib';
import { AuthService } from './auth.service';
import { IUserPermissionType, User } from '@core/models';
import { MongoUser } from '@core/models/mongo';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  constructor(private http: HttpClient, private apiService: ApiService, private authService: AuthService) {}

  getCompanyByMongoId(mongoId: string): Observable<IApiResponse> {
    return this.http
      .get<IApiResponse>(`${this.apiService.companies}/${mongoId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  updateCompany(company: Partial<ICompany>, skipExistsCheck = false): Observable<IApiResponse<ICompany>> {
    const { _id: id, ...params } = company;
    return this.http
      .put<IApiResponse<ICompany>>(`${this.apiService.companies}/${id}?skipExistsCheck=${skipExistsCheck}`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getCompanyUsers(id: string): Observable<IApiResponse<User[]>> {
    return this.http
      .get<IApiResponse<User[]>>(`${this.apiService.companyUsers}/${id}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getCompanyUsersSkipDriverIntervalCheck(id: string): Observable<IApiResponse<MongoUser[]>> {
    return this.http
      .get<IApiResponse<MongoUser[]>>(`${this.apiService.companyUsers}/${id}/skip-driver-interval-check`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  createStripeCustomer(companyId: string): Observable<IApiResponse> {
    return this.http
      .post<IApiResponse>(`${this.apiService.payments}/customers/${companyId}`, null)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  // Check Dot & MC numbers before saving
  checkDotNumber(params: { dotNumber: string; type: string }): Observable<any> {
    return this.http.post<IApiResponse>(`${this.apiService.companies}/check-dotNumber`, params);
  }

  checMcNumber(params: { mcNumber: string; type: string; checMcCheckAudit?: boolean }): Observable<any> {
    return this.http.post<IApiResponse>(`${this.apiService.companies}/check-mcNumber`, params);
  }

  updateCompanyPermissions(companyId: string, payload: IUserPermissionType[]): Observable<IApiResponse> {
    return this.http
      .put<IApiResponse>(`${this.apiService.companies}/${companyId}/permissions`, { uiPermissions: payload })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  // Export a specific company to ES
  exportToES(companyId: string, update = false): Observable<IApiResponse> {
    return this.http
      .post<IApiResponse>(`${this.apiService.companies}/${companyId}/export-to-es`, { update })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  deleteDocument(companyId: string, docType: string): Observable<IApiResponse> {
    return this.http
      .post<IApiResponse>(`${this.apiService.companies}/${companyId}/documents?type=${docType}`, {
        docs: [],
      })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }
}
